import React, { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import { withoutAuth } from '../../hooks/useAuth';
import { Link, useSearchParams } from 'react-router-dom';
import Logo from "../../assets/img/favicon.png";
import { useNavigate } from 'react-router-dom';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const [newPassword, setNewPassword] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestPassword, setIsRequestPassword] = useState(!token); // Determine view based on token
  const navigate = useNavigate();

  const handleNewPasswordChange = (event) => {
    setNewPassword(event.target.value);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
  };


  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(process.env.REACT_APP_BACKEND_URI + 'user/requestResetPassword', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail }),
      });
      if (response.ok) {
        setAlertMessage('Password reset email sent. Check your inbox.');
        setAlertType('success');
      } else {
        const data = await response.json();
        setAlertMessage(data.message || 'Request failed.');
        setAlertType('error');
      }
    } catch (error) {
      setAlertMessage('An error occurred. Please try again later.');
      setAlertType('error');
    } finally {
      setIsLoading(false);
      setAlertOpen(true);
    }
  };

  const handleSubmitReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (newPassword !== confirmPassword) {
      setAlertMessage('Passwords do not match.');
      setAlertType('error');
      setAlertOpen(true);
      setIsLoading(false);
      return;
    }
    try {
      const response = await fetch(process.env.REACT_APP_BACKEND_URI + 'user/resetPassword', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, newPassword }),
      });
      if (response.ok) {
        setAlertMessage('Password reset successfully!');
        setAlertType('success');
        navigate('/login');
      } else {
        const data = await response.json();
        setAlertMessage(data.message || 'Reset failed.');
        setAlertType('error');
      }
    } catch (error) {
      setAlertMessage('An error occurred. Please try again later.');
      setAlertType('error');
    } finally {
      setIsLoading(false);
      setAlertOpen(true);
    }
  };

  useEffect(() => {
    if (alertOpen) {
      const timer = setTimeout(() => {
        setAlertOpen(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [alertOpen]);

  return (
    <section className='h-[100vh] bg-pattern'>
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <Link to={"/"}>
            <img alt="Marrocco ART" src={Logo} className="mx-auto h-16 w-auto" />
          </Link>
          <h2 className="mt-10 text-center text-2xl font-bold tracking-tight text-gray-900">
            {isRequestPassword ? 'Request Password Reset' : 'Reset Password'}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          {isRequestPassword ? (
            <form onSubmit={handleSubmitRequest} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900">Email</label>
                <input
                  type="email"
                  id="email"
                  value={userEmail || ''}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="block w-full mt-2 px-4 py-2 border rounded-lg"
                  placeholder="Email Address"
                  required
                />
              </div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md bg-[#08a5ec] px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-[#126eeb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#08a5ec]"
                disabled={isLoading}
              >
                {isLoading ? <div>Loading...</div> : 'Request Reset'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmitReset} className="space-y-6">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-900">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  className="block w-full mt-2 px-4 py-2 border rounded-lg"
                  placeholder="New Password"
                  required
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  className="block w-full mt-2 px-4 py-2 border rounded-lg"
                  placeholder="Confirm Password"
                  required
                />
              </div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md bg-[#08a5ec] px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-[#126eeb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#08a5ec]"
                disabled={isLoading}
              >
                {isLoading ? <div>Loading...</div> : 'Reset Password'}
              </button>
            </form>
          )}
          <p className="mt-10 text-center text-sm text-gray-500">
            {isRequestPassword ? 'Already have a password?' : 'Request password reset?'}
            <Link to={isRequestPassword ? '/login' : `/reset-password?email=${email}`} className="font-semibold text-[#08a5ec] hover:text-[#126eeb] ml-1.5">
              {isRequestPassword ? 'Login' : 'Request Reset'}
            </Link>
          </p>
        </div>
      </div>
      <div className={`absolute top-24 right-0 p-4 ${alertOpen ? 'block' : 'hidden'}`}>
        <Alert severity={alertType} onClose={() => setAlertOpen(false)}
          sx={{ backgroundColor: '#1121212', color: '#1ffffff', border: '1px solid #1ffffff' }}>
          {alertMessage}
        </Alert>
      </div>
    </section>
  );
}

export default withoutAuth(ResetPassword);